import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";

const FILTERS = [
  { label: "Shop All", params: {} },
  { label: "Adult", params: { audience: "Adult" } },
  { label: "Women's", params: { audience: "Women" } },
  { label: "Youth", params: { audience: "Youth" } },
  { label: "No-Gi", params: { category: "No-Gi" } },
  { label: "Gis", params: { category: "Gis" } },
  { label: "Accessories", params: { category: "Accessories" } },
  { label: "Limited Edition", params: { collection: "limited" } },
  { label: "Custom", params: { collection: "custom" } },
];
const KEYS = ["category", "audience", "collection", "round"];
const SORTS = {
  featured: () => 0,
  "price-asc": (a, b) => a.retail_price - b.retail_price,
  "price-desc": (a, b) => b.retail_price - a.retail_price,
  name: (a, b) => a.name.localeCompare(b.name),
};
const AUD_ORDER = { Adult: 0, Women: 1, Youth: 2 };

function titleFor(p) {
  if (p.collection === "limited") return "Limited Edition";
  if (p.audience === "Women") return "Women's";
  if (p.audience) return p.audience;
  if (p.category) return p.category;
  return p.round ? "Winter '26 · Round 1" : "Shop Paragon";
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const [term, setTerm] = useState(params.get("search") || "");
  const current = Object.fromEntries(KEYS.map((k) => [k, params.get(k)]).filter(([, v]) => v));
  const search = params.get("search") || "";
  const qs = new URLSearchParams({ ...current, ...(search ? { search } : {}) }).toString();

  useEffect(() => {
    setLoading(true);
    api.get(`/products?${qs}`).then(({ data }) => setProducts(data)).finally(() => setLoading(false));
  }, [qs]);

  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "featured") return arr.sort((a, b) => (AUD_ORDER[a.audience] ?? 3) - (AUD_ORDER[b.audience] ?? 3));
    return arr.sort(SORTS[sort]);
  }, [products, sort]);

  const isActive = (f) => JSON.stringify(f.params) === JSON.stringify(Object.fromEntries(Object.entries(current).filter(([k]) => k !== "round")));
  const submitSearch = (e) => {
    e.preventDefault();
    const next = { ...current };
    if (term.trim()) next.search = term.trim();
    setParams(next);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-10">
      <div className="mb-6">
        <p className="font-mono text-yellow-400 uppercase tracking-widest text-xs">Paragon Team Store</p>
        <h1 data-testid="shop-title" className="font-heading font-black uppercase text-4xl lg:text-5xl text-white mt-1">
          {search ? `Results for “${search}”` : titleFor(current)}
        </h1>
        <p data-testid="shop-count" className="text-zinc-500 mt-2 font-mono text-sm">{products.length} products</p>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-6 px-6 lg:mx-0 lg:px-0">
        {FILTERS.map((f) => (
          <button key={f.label} data-testid={`shop-filter-${f.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setParams(f.params)}
            className={`shrink-0 px-4 py-2 rounded-full text-xs font-mono uppercase tracking-wider border transition-colors ${isActive(f) && !search ? "border-yellow-400 bg-yellow-400 text-black font-bold" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <form onSubmit={submitSearch} className="flex-1 relative">
          <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="shop-search-input" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search hoodies, gis, shorts…"
            className="w-full bg-[#121216] border border-white/15 rounded-sm pl-9 pr-24 py-2.5 text-base sm:text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-yellow-400" />
          <button data-testid="shop-search-submit" className="absolute right-1 top-1 bottom-1 px-4 bg-yellow-400 text-black text-xs font-bold uppercase rounded-sm">Search</button>
        </form>
        <select data-testid="shop-sort" value={sort} onChange={(e) => setSort(e.target.value)}
          className="bg-[#121216] border border-white/15 rounded-sm px-3 py-2.5 text-base sm:text-sm text-white focus:outline-none focus:border-yellow-400">
          <option value="featured">Sort: Featured</option>
          <option value="price-asc">Price: Low to High</option>
          <option value="price-desc">Price: High to Low</option>
          <option value="name">Name: A–Z</option>
        </select>
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mono py-20 text-center">Loading gear…</div>
      ) : sorted.length === 0 ? (
        <div data-testid="shop-empty" className="text-zinc-500 font-mono py-20 text-center">No products found.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
          {sorted.map((p) => (
            <ProductCard key={p.id} product={p} colorway={current.collection === "limited" ? "limited" : null} />
          ))}
        </div>
      )}
    </div>
  );
}
